import type * as React from 'react'

// Epic 52 Phase 02 — icon normalisation contract
// (.claude/epics/52-token-and-icon-foundation/reference/ICON-NORMALISATION.md §(b)/(c))
// on `icon / Default` (16px export at node 102:1672; 12px export at node 102:1677, not touched).
//
// NOTE (Phase 00 contract gap, still open): the contract's §(c) specifies a shared
// `SvgIconProps` type at `apps/web/src/components/icons/types.ts`. That file does not exist and
// no phase currently owns creating it. This component must not create that shared module
// unprompted, so the prop type is inlined here rather than imported, matching every other icon
// component shipped so far.
type SvgIconProps = Omit<React.SVGProps<SVGSVGElement>, 'children'> & {
  /** Accessible name. Omit for a purely decorative icon (default: aria-hidden). */
  title?: string
  /** Rendered size in px. Defaults to 16 — the artwork's native/authored size. */
  size?: 12 | 16
}

// D-ICON-1 (2026-08-25): the 12px and 16px `Default` exports were measured
// coordinate-by-coordinate (228 coordinates compared) and found to be a uniform 4/3 scale of one
// another, ratio spread 0.000501 — equal to within float rounding. Per the owner decision this
// single component keeps the 16px artwork and viewBox (more coordinate precision than the 12px
// export) and lets the `size` prop drive only the rendered width/height attributes; the viewBox
// stays "0 0 16 16" regardless of `size`, so the 12px render is the SVG user agent uniformly
// scaling this same path data by 12/16 — mathematically identical to what the separate 12px
// artwork was (the two differed only by that same uniform scale).
export function IconDefault({ title, size = 16, ...rest }: SvgIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path
        d="M9.31148 13.0872C9.71234 12.9832 10.1214 13.2234 10.2255 13.6243C10.3293 14.025 10.0891 14.4342 9.68843 14.5383C8.58124 14.8258 7.41869 14.8257 6.31148 14.5383C5.91072 14.4342 5.6705 14.0251 5.77437 13.6243C5.8785 13.2234 6.28754 12.9831 6.68843 13.0872C7.54839 13.3104 8.45153 13.3104 9.31148 13.0872Z"
        fill="currentColor"
      />
      <path
        d="M2.01851 8.88307C2.41761 8.77304 2.83008 9.00745 2.94039 9.40651C3.17701 10.2629 3.62878 11.0447 4.25191 11.678C4.54235 11.9731 4.53895 12.4479 4.2441 12.7385C3.94896 13.0289 3.4741 13.0257 3.18355 12.7307C2.38103 11.9153 1.7997 10.9077 1.49507 9.80494C1.38514 9.40591 1.61955 8.9934 2.01851 8.88307Z"
        fill="currentColor"
      />
      <path
        d="M13.0595 9.40651C13.1701 9.00747 13.5833 8.77367 13.9824 8.88405C14.3813 8.99468 14.6152 9.40785 14.5048 9.8069C14.1999 10.9091 13.6186 11.9158 12.8164 12.7307C12.5258 13.0255 12.0509 13.0288 11.7558 12.7385C11.4608 12.4479 11.4575 11.9731 11.748 11.678C12.3712 11.0449 12.8227 10.2627 13.0595 9.40651Z"
        fill="currentColor"
      />
      <path
        d="M11.7558 3.26198C12.0509 2.97152 12.5258 2.97488 12.8164 3.26979C13.6188 4.08518 14.2002 5.09285 14.5048 6.19557C14.6146 6.59457 14.3804 7.00718 13.9814 7.11745C13.5825 7.22742 13.1701 6.99359 13.0595 6.59498C12.8229 5.73853 12.3712 4.95587 11.748 4.32252C11.4576 4.02745 11.461 3.55257 11.7558 3.26198Z"
        fill="currentColor"
      />
      <path
        d="M3.18355 3.26979C3.47408 2.97495 3.94898 2.97173 4.2441 3.26198C4.53903 3.55259 4.54242 4.02743 4.25191 4.32252C3.6288 4.9556 3.17716 5.73785 2.94039 6.59401C2.82978 6.99294 2.4166 7.22683 2.01754 7.11647C1.61875 7.00577 1.38475 6.5926 1.49507 6.19362C1.79993 5.0914 2.38128 4.08477 3.18355 3.26979Z"
        fill="currentColor"
      />
      <path
        d="M6.31148 1.46217C7.41875 1.17473 8.58117 1.17471 9.68843 1.46217C10.0891 1.56641 10.3296 1.97544 10.2255 2.37623C10.1213 2.77683 9.71218 3.0172 9.31148 2.91334C8.45148 2.6901 7.54843 2.69011 6.68843 2.91334C6.28767 3.01739 5.87865 2.77687 5.77437 2.37623C5.67031 1.97534 5.91061 1.56628 6.31148 1.46217Z"
        fill="currentColor"
      />
    </svg>
  )
}
